import { isEmpty, orderBy, keyBy, mapValues, groupBy } from 'lodash';
import { ALL_INTERESTS } from '../constants';

/**
 * Validates data response and throws appropriate errors for invalid states
 */
export const throwForInvalidDataResponse = (data) => {
  if (!data) throw new Error('noData');
  switch (data.status) {
    case 'noData':
      throw new Error('noData');
    case 'segment too wide':
    case 'tooWide':
      throw new Error('tooWide');
    case 'insufficient data':
    case 'tooNarrow':
      throw new Error('tooNarrow');
  }
  if (isEmpty(data.words) && isEmpty(data.insights)) throw new Error('noData');
  return data;
};

/**
 * Generates tooltip string for interest skew
 */
export const genInterestSkewTooltipStr = ({ phrase, 'uniqueness-index': skew }) => {
  switch (+skew.toFixed(1)) {
    case 0:
      return `Consumers of ${phrase} are unlikely to be in the target audience`;
    case 1:
      return `Consumers of ${phrase} are as likely to be in the target audience as the average consumer`;
    default:
      const [multiplier, compare] = skew > 1 ? [skew, 'more'] : [1 / skew, 'less'];
      return `Consumers of ${phrase} are ×${multiplier.toFixed(
        1
      )} times ${compare} likely to be in the target audience than the average consumer`;
  }
};

/**
 * Generates tooltip string for interest consumption
 */
export const genInterestConsumptionTooltipStr = ({ phrase, 'interest-portion': consumption }, interestType) => {
  const clientsVerb = interestType === 'websites' ? 'consumed' : 'are interested in';
  return `${consumption.toFixed(1)}% of the target audience ${clientsVerb} ${phrase}`;
};

/**
 * Generates combined tooltip string for interest (consumption + skew)
 */
export const genInterestTooltipStr = (obj, interestType) =>
  genInterestConsumptionTooltipStr(obj, interestType) + '<br/>' + genInterestSkewTooltipStr(obj);

/**
 * Transforms raw topics data into display format
 */
export const transformTopicsData = (topics) =>
  topics.map((topic) => ({
    ...topic,
    id: topic.id,
    percents: topic['insight-portion'],
    percentValue: topic['insight-portion'].toFixed(1),
    value: topic['uniqueness-index'].toFixed(1),
    tooltip: genInterestTooltipStr(topic),
  }));

/**
 * Transforms raw interests data into chart and table formats
 */
export const transformInterestsData = (interests, interestType, addUrlsBasedOnInterestText = false) => {
  const forChart = interests.map((interest) => ({
    ...interest,
    id: interest['id'] || interest['phrase-id'],
    title: interest['phrase'],
    portion: interest['interest-portion'],
    composition: interest['uniqueness-index'],
    value: interest['interest-portion'].toFixed(1) + '% | ' + interest['uniqueness-index'].toFixed(1),
    url: addUrlsBasedOnInterestText ? 'http://' + interest['phrase'] : undefined,
    tooltip: genInterestTooltipStr(interest, interestType),
    highlighted: false, // Default to false for consistent sorting
  }));

  const forTable = interests.map((interest) => ({
    ...interest,
    id: interest['id'] || interest['phrase-id'],
    skewTooltip: genInterestSkewTooltipStr(interest),
  }));

  return mapValues({ forChart, forTable }, (objs) => Object.assign(groupBy(objs, 'topic'), { [ALL_INTERESTS]: objs }));
};

/**
 * Sorts and normalizes interests data for display
 */
export const sortAndNormalizeInterests = ({ forChart, forTable }, sortType, infraUtil) => {
  if (isEmpty(forChart) || isEmpty(forTable)) return { forChart, forTable };

  forChart.forEach((i) => (i ? (i.percents = i[sortType]) : null));
  // Sort by highlighted (desc) first, then by sortType (desc) - highlighted items at top
  let sortedChart = orderBy(forChart, ['highlighted', sortType], ['desc', 'desc']);
  // sortedChart = sortedChart.slice(0, 100); // chart cannot handle all interests - too slow
  sortedChart = infraUtil.normalize(sortedChart, 'percents', 'percents');

  return { forChart: sortedChart, forTable };
};

/**
 * Indexes interests by ID and merges with previous interests by view
 */
export const indexByIdAndMergeByView = (interestsByView, lastInterestsByIdByView) => {
  const interestsByIdByView = mapValues(interestsByView, (interests) => keyBy(interests, 'id'));
  if (!lastInterestsByIdByView || isEmpty(lastInterestsByIdByView)) return interestsByView;

  const mergedViews = mapValues(lastInterestsByIdByView, (obj, view) =>
    Object.assign({}, obj, interestsByIdByView[view])
  );
  return mapValues(mergedViews, (interests) => Object.values(interests));
};

/**
 * Gets the no data message for a specific interest type
 */
export const getNoDataMsg = (interestType, interestTypesMap) => {
  const typeInfo = interestTypesMap[interestType];
  return `Sorry, we couldn't find relevant ${
    typeInfo ? typeInfo.plural : 'interests'
  } on this topic for your target audience.`;
};

/**
 * Gets the interest type based on selected tab
 */
export const getInterestType = (selectedTab, tabsNames, interestTypes) => {
  if ([tabsNames?.categories, tabsNames?.purchaseAffinities].includes(selectedTab?.value)) {
    return 'categories';
  }
  return interestTypes[0].value;
};

/**
 * Continues promise chain if error is noData or tooNarrow
 */
export const continueIfNoData = (error) => {
  const errMsg = error?.message || error;
  if (errMsg === 'noData' || errMsg === 'tooNarrow') {
    return Promise.resolve({});
  }
  return Promise.reject(error);
};
